/**
 * AI draft card — the one look every AI draft uses: spark icon, "AI draft" label, the draft, its sources, and
 * Use · Edit · Discard. The card never saves or signs: Use hands the (possibly edited) text to the caller, which
 * puts it in the person's own field; Discard drops it. Buttons render only for the handlers given.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Skeleton, Space, Tag, theme } from 'antd';
import { Check, Pencil, RefreshCw, Sparkles, X } from 'lucide-react';

export function AiDraftTag() {
  return <Tag color="purple" icon={<Sparkles size={11} aria-hidden style={{ marginRight: 4 }} />}>AI draft</Tag>;
}

export default function AiDraftCard({
  title, text, loading, sources, children, onUse, onDiscard, onRefresh, editable, testId, footer,
}) {
  const { token } = theme.useToken();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text || '');
  useEffect(() => { setValue(text || ''); setEditing(false); }, [text]);
  return (
    <section className="cpv-card cpv-pad" data-testid={testId} aria-label={`AI draft · ${title}`} style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={16} color={token.colorPrimary} aria-hidden />
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: token.colorText }}>{`AI draft · ${title}`}</h2>
        <span style={{ flex: 1 }} />
        {onRefresh ? (
          <Button size="small" type="text" icon={<RefreshCw size={13} aria-hidden />} onClick={onRefresh} loading={loading && !!text} data-testid={`${testId}-refresh`}>Refresh</Button>
        ) : null}
      </div>
      {loading && !text ? <Skeleton active paragraph={{ rows: 2 }} title={false} /> : null}
      {text !== undefined && text !== null && !(loading && !text) ? (
        editing ? (
          <Input.TextArea value={value} onChange={(e) => setValue(e.target.value)} autoSize={{ minRows: 3 }} style={{ marginTop: 8 }} data-testid={`${testId}-edit-text`} />
        ) : (
          text ? <p style={{ margin: '8px 0 0', color: token.colorText, whiteSpace: 'pre-wrap' }} data-testid={`${testId}-text`}>{value}</p> : null
        )
      ) : null}
      {children}
      {sources && sources.length ? (
        <div style={{ marginTop: 4, fontSize: 12, color: token.colorTextTertiary }} data-testid={`${testId}-sources`}>
          Sources: {sources.map((s) => s.label).filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {footer}
      {(onUse || onDiscard || editable) && (text || children) ? (
        <Space size={4} style={{ marginTop: 8 }}>
          {onUse ? <Button size="small" type="primary" icon={<Check size={13} aria-hidden />} onClick={() => onUse(value)} data-testid={`${testId}-use`}>Use</Button> : null}
          {editable && text ? (
            <Button size="small" icon={<Pencil size={13} aria-hidden />} onClick={() => setEditing((e) => !e)} data-testid={`${testId}-edit`}>{editing ? 'Done' : 'Edit'}</Button>
          ) : null}
          {onDiscard ? <Button size="small" type="text" icon={<X size={13} aria-hidden />} onClick={onDiscard} data-testid={`${testId}-discard`}>Discard</Button> : null}
        </Space>
      ) : null}
    </section>
  );
}

AiDraftCard.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  loading: PropTypes.bool,
  sources: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string })),
  children: PropTypes.node,
  footer: PropTypes.node,
  onUse: PropTypes.func,
  onDiscard: PropTypes.func,
  onRefresh: PropTypes.func,
  editable: PropTypes.bool,
  testId: PropTypes.string,
};

AiDraftCard.defaultProps = {
  text: undefined, loading: false, sources: [], children: null, footer: null, onUse: undefined, onDiscard: undefined,
  onRefresh: undefined, editable: false, testId: 'ai-draft',
};
